/**
 * Catmull-Rom spline that passes through all control points.
 * Internally converts each segment to a cubic Bezier for smooth evaluation.
 * Supports position, tangent (direction), and arc-length parameterization.
 */

interface Vec2 { x: number; y: number; }

interface BezierSegment {
  p0: Vec2; p1: Vec2; p2: Vec2; p3: Vec2;
  arcLength: number;
}

export class Spline {
  private segments: BezierSegment[] = [];
  totalLength = 0;

  /** Build spline from an ordered list of waypoints (at least 2). */
  readonly valid: boolean;

  constructor(points: [number, number][]) {
    if (points.length < 2) {
      this.valid = false;
      return;
    }
    this.valid = true;

    const pts: Vec2[] = points.map(([x, y]) => ({ x, y }));

    // Duplicate first and last points so Catmull-Rom covers all segments
    const extended: Vec2[] = [
      pts[0]!,
      ...pts,
      pts[pts.length - 1]!,
    ];

    for (let i = 0; i < extended.length - 3; i++) {
      const p0 = extended[i]!;
      const p1 = extended[i + 1]!;
      const p2 = extended[i + 2]!;
      const p3 = extended[i + 3]!;

      // Convert Catmull-Rom to cubic Bezier control points
      // Using tension = 0 (standard Catmull-Rom)
      const b0 = p1;
      const b1: Vec2 = {
        x: p1.x + (p2.x - p0.x) / 6,
        y: p1.y + (p2.y - p0.y) / 6,
      };
      const b2: Vec2 = {
        x: p2.x - (p3.x - p1.x) / 6,
        y: p2.y - (p3.y - p1.y) / 6,
      };
      const b3 = p2;

      const arcLength = this.estimateArcLength(b0, b1, b2, b3, 16);
      this.segments.push({ p0: b0, p1: b1, p2: b2, p3: b3, arcLength });
      this.totalLength += arcLength;
    }
  }

  get segmentCount(): number { return this.segments.length; }

  /**
   * Get position at distance `d` along the spline (0 to totalLength).
   */
  positionAt(d: number): [number, number] {
    if (this.segments.length === 0) return [0, 0];

    d = Math.max(0, Math.min(this.totalLength, d));

    let remaining = d;
    for (const seg of this.segments) {
      if (remaining <= seg.arcLength) {
        const t = seg.arcLength > 0 ? remaining / seg.arcLength : 0;
        return this.evalBezier(seg, t);
      }
      remaining -= seg.arcLength;
    }

    // End of spline
    const last = this.segments[this.segments.length - 1]!;
    return this.evalBezier(last, 1);
  }

  /**
   * Get tangent direction (normalized) at distance `d`.
   */
  tangentAt(d: number): [number, number] {
    if (this.segments.length === 0) return [1, 0];

    d = Math.max(0, Math.min(this.totalLength, d));

    let remaining = d;
    for (const seg of this.segments) {
      if (remaining <= seg.arcLength) {
        const t = seg.arcLength > 0 ? remaining / seg.arcLength : 0;
        return this.evalBezierTangent(seg, t);
      }
      remaining -= seg.arcLength;
    }

    const last = this.segments[this.segments.length - 1]!;
    return this.evalBezierTangent(last, 1);
  }

  /** Evaluate cubic Bezier position at parameter t (0..1) */
  private evalBezier(s: BezierSegment, t: number): [number, number] {
    const u = 1 - t;
    const uu = u * u;
    const tt = t * t;
    const x = uu * u * s.p0.x + 3 * uu * t * s.p1.x + 3 * u * tt * s.p2.x + tt * t * s.p3.x;
    const y = uu * u * s.p0.y + 3 * uu * t * s.p1.y + 3 * u * tt * s.p2.y + tt * t * s.p3.y;
    return [x, y];
  }

  /** Evaluate cubic Bezier tangent (first derivative, normalized) */
  private evalBezierTangent(s: BezierSegment, t: number): [number, number] {
    const u = 1 - t;
    const dx = 3 * u * u * (s.p1.x - s.p0.x) + 6 * u * t * (s.p2.x - s.p1.x) + 3 * t * t * (s.p3.x - s.p2.x);
    const dy = 3 * u * u * (s.p1.y - s.p0.y) + 6 * u * t * (s.p2.y - s.p1.y) + 3 * t * t * (s.p3.y - s.p2.y);
    const len = Math.hypot(dx, dy);
    if (len < 1e-10) return [1, 0]; // degenerate: use rightward fallback
    return [dx / len, dy / len];
  }

  /** Approximate arc length of a cubic Bezier by subdividing into `steps` line segments */
  private estimateArcLength(p0: Vec2, p1: Vec2, p2: Vec2, p3: Vec2, steps: number): number {
    let length = 0;
    let prevX = p0.x;
    let prevY = p0.y;

    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const u = 1 - t;
      const uu = u * u;
      const tt = t * t;
      const x = uu * u * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + tt * t * p3.x;
      const y = uu * u * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + tt * t * p3.y;
      length += Math.hypot(x - prevX, y - prevY);
      prevX = x;
      prevY = y;
    }

    return length;
  }
}
